/** Plain-text extraction for draft summaries (no DOM dependency). */
export function stripDraftHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Detect the kind of filing the user asked for (e.g. "an appeal"). */
export function inferDraftKindPhrase(prompt: string): string {
  const p = prompt.trim().toLowerCase();
  if (/\bappeals?\b/.test(p)) return 'an appeal';
  if (/\bplaints?\b/.test(p)) return 'a plaint';
  if (/\bpetitions?\b/.test(p)) return 'a petition';
  if (/\bapplications?\b/.test(p)) return 'an application';
  if (/\baffidavits?\b/.test(p)) return 'an affidavit';
  if (/\bwritten\s+statements?\b/.test(p)) return 'a written statement';
  if (/\brejoinders?\b/.test(p)) return 'a rejoinder';
  if (/\breplies?\b/.test(p)) return 'a reply';
  if (/\bmemos?(?:randum)?\b/.test(p)) return 'a memorandum';
  if (/\bcross[\s-]?objections?\b/.test(p)) return 'cross-objections';
  if (/\bbails?\b/.test(p)) return 'a bail application';
  return 'a draft';
}

/**
 * True only when the user is altering an existing legal identifier —
 * not when they are simply asking for a new draft.
 */
export function isHighImpactIdentifierEdit(prompt: string): boolean {
  const normalized = prompt.trim().toLowerCase();
  const asksToChange = /\b(correct|fix|replace|update|change|rename)\b/.test(normalized);
  if (!asksToChange) return false;
  return /\b(lawyer|advocate|counsel|party|petitioner|respondent|plaintiff|defendant|court|judge|case\s*number|citation|date|amount|prayer|signature|jurisdiction|name)\b/.test(
    normalized,
  );
}

/** Main side-panel line for a full-document proposal. */
export function buildDraftChangeReason(prompt: string, highImpact: boolean): string {
  if (highImpact) {
    return 'I updated a legal detail in your document. Please verify the changed names, references, and dates before accepting.';
  }
  const kind = inferDraftKindPhrase(prompt);
  return `I've drafted ${kind} from the available information. Please make changes and additions as needed.`;
}

/**
 * One short line about facts already used or gaps still open.
 * Prefer placeholders ([...]) over structural guesswork.
 */
export function buildDraftFactsLine(proposedHtml: string): string | null {
  const placeholders = [
    ...new Set(
      (proposedHtml.match(/\[[^\]]{2,80}\]/g) ?? []).map((p) =>
        p.replace(/^\[|\]$/g, '').replace(/\s+/g, ' ').trim(),
      ),
    ),
  ].filter(Boolean);

  if (placeholders.length > 0) {
    const shown = placeholders.slice(0, 3);
    const more = placeholders.length - shown.length;
    let list: string;
    if (more > 0) {
      list = `${shown.join(', ')}, and ${more} more`;
    } else if (shown.length === 1) {
      list = shown[0];
    } else if (shown.length === 2) {
      list = `${shown[0]} and ${shown[1]}`;
    } else {
      list = `${shown.slice(0, -1).join(', ')}, and ${shown[shown.length - 1]}`;
    }
    return `Still needs: ${list}.`;
  }

  const text = stripDraftHtml(proposedHtml);
  const hasParties =
    /\b(Appellant|Respondent|Plaintiff|Defendant|Petitioner|Accused)\b/i.test(text);
  const hasPrayer =
    /\b(prayer|relief sought|wherefore|it is most respectfully prayed)\b/i.test(text);

  if (hasParties && hasPrayer) {
    return 'Party details and a prayer are included from what was available — adjust any facts that need correcting.';
  }
  if (hasParties) {
    return 'Party details are included from what was available — add or correct facts as needed.';
  }
  return 'Review the facts in the draft and add anything that is missing.';
}

/** Side-panel bullets: at most one facts/gaps line. */
export function buildDraftKeyPoints(proposedHtml: string, _prompt: string): string[] {
  const line = buildDraftFactsLine(proposedHtml);
  return line ? [line] : [];
}
